import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect } from 'react';
import { lockScroll, unlockScroll } from '@/providers/LenisProvider';

type LightboxProps = {
  images: string[];
  /** Current open index, or null when closed. */
  index: number | null;
  onChange: (index: number | null) => void;
};

/** Accessible carousel lightbox: arrows, keyboard (←/→/Esc), counter, backdrop close. */
export function Lightbox({ images, index, onChange }: LightboxProps) {
  const open = index !== null;
  const close = useCallback(() => onChange(null), [onChange]);
  const go = useCallback(
    (dir: number) => {
      if (index === null) return;
      onChange((index + dir + images.length) % images.length);
    },
    [index, images.length, onChange],
  );

  useEffect(() => {
    if (!open) return;
    lockScroll();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      unlockScroll();
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, go]);

  const current = index !== null ? (images[index] ?? '') : '';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Gallery"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={close}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/95 p-4 text-paper"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 text-xl transition-colors hover:bg-paper hover:text-ink"
          >
            ✕
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label="Previous image"
            className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 transition-colors hover:bg-paper hover:text-ink md:left-8"
          >
            ←
          </button>

          <motion.img
            key={index}
            src={current}
            alt={`Image ${(index ?? 0) + 1} of ${images.length}`}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[85vh] max-w-[90vw] object-contain"
          />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            aria-label="Next image"
            className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 transition-colors hover:bg-paper hover:text-ink md:right-8"
          >
            →
          </button>

          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm tracking-[0.2em]">
            {String((index ?? 0) + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
